/**
 * Telemetry client — singleton com batch queue.
 *
 * - Eventos são enfileirados em memória
 * - Flush automático a cada 10s ou quando a fila atinge 20 eventos
 * - Flush forçado em `beforeunload` via sendBeacon (sobrevive a fechamento de aba)
 * - Super_admins NÃO geram eventos (filtro feito pelo TelemetryProvider antes
 *   de chamar este client; aqui é fail-safe via flag)
 *
 * Não importar diretamente em components — use `useTelemetry()` ou
 * o TelemetryProvider que mantém a referência ativa.
 */

import { supabase } from "@/lib/supabase/client"

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const TRACK_ENDPOINT = `${SUPABASE_URL}/functions/v1/track-event`

const FLUSH_INTERVAL_MS = 10_000
const MAX_QUEUE_SIZE = 20
const SESSION_STORAGE_KEY = "creditos_telemetry_session"

export type TelemetryEventType = "pageview" | "action" | "error" | "web_vital"

export interface TelemetryEvent {
  event_type: TelemetryEventType
  event_name: string
  properties?: Record<string, unknown>
  page_path?: string
  page_title?: string
  user_agent?: string
  viewport_w?: number
  viewport_h?: number
  occurred_at: string
}

interface ClientConfig {
  enabled: boolean
}

/**
 * Contexto do usuário logado, mantido no singleton para que quem está FORA
 * da árvore de providers consiga anexá-lo a um evento. É o caso do
 * ErrorBoundary: ele vive acima do TelemetryProvider (App.tsx), então não
 * consegue ler `useAuth()` — sem isto, um erro de tela chegaria sem o papel
 * de quem sofreu. Só `role` é guardado; nada que identifique a pessoa.
 */
interface UserContext {
  role: string | null
}

class TelemetryClient {
  private queue: TelemetryEvent[] = []
  private sessionId: string
  private flushTimer: number | null = null
  private config: ClientConfig = { enabled: false }
  private context: UserContext = { role: null }
  private cachedJwt: string | null = null
  private jwtExpiresAt: number = 0

  constructor() {
    this.sessionId = this.getOrCreateSessionId()
  }

  /** Habilita/desabilita o client. Desabilitar dropa eventos enfileirados. */
  configure(config: ClientConfig): void {
    this.config = config
    if (!config.enabled) {
      this.queue = []
      this.stopFlushTimer()
    } else if (!this.flushTimer) {
      this.startFlushTimer()
    }
  }

  isEnabled(): boolean {
    return this.config.enabled
  }

  /** Registra o papel do usuário logado (sem PII). Chamado pelo TelemetryProvider. */
  setContext(context: UserContext): void {
    this.context = context
  }

  /** Papel do usuário logado, para quem precisa anexá-lo manualmente a um evento. */
  getRole(): string | null {
    return this.context.role
  }

  getSessionId(): string {
    return this.sessionId
  }

  /** Adiciona evento à fila. No-op se desabilitado. */
  track(event: Omit<TelemetryEvent, "occurred_at" | "user_agent" | "viewport_w" | "viewport_h" | "page_path" | "page_title"> & {
    page_path?: string
    page_title?: string
  }): void {
    if (!this.config.enabled) return
    if (typeof window === "undefined") return

    const enriched: TelemetryEvent = {
      ...event,
      occurred_at: new Date().toISOString(),
      page_path: event.page_path ?? window.location.pathname,
      page_title: event.page_title ?? document.title,
      user_agent: navigator.userAgent.slice(0, 300),
      viewport_w: window.innerWidth,
      viewport_h: window.innerHeight,
    }

    this.queue.push(enriched)

    if (this.queue.length >= MAX_QUEUE_SIZE) {
      void this.flush()
    }
  }

  /** Envia eventos enfileirados imediatamente. */
  async flush(): Promise<void> {
    if (this.queue.length === 0) return
    const batch = this.queue.splice(0, this.queue.length)
    const jwt = await this.getJwt()
    if (!jwt) {
      // Sessão ainda não resolvida: devolve o lote pra fila em vez de descartar.
      // Sem isto, um evento gerado logo após o load (justamente quando a tela
      // quebra cedo) era perdido em silêncio. Limite evita crescimento infinito
      // se a sessão nunca vier.
      this.queue = [...batch, ...this.queue].slice(0, MAX_QUEUE_SIZE * 2)
      return
    }

    try {
      await fetch(TRACK_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          events: batch,
        }),
        keepalive: true,
      })
    } catch (err) {
      // Falha silenciosa — telemetria não pode quebrar UX. Loss aceitável.
      console.warn("[telemetry] flush failed", err)
    }
  }

  /** Flush via sendBeacon (sobrevive ao unload). Chama no beforeunload/pagehide. */
  flushBeacon(): void {
    if (this.queue.length === 0) return
    const batch = this.queue.splice(0, this.queue.length)
    const jwt = this.cachedJwt
    if (!jwt) return

    try {
      const body = JSON.stringify({
        session_id: this.sessionId,
        events: batch,
        auth_token: jwt,
      })
      // sendBeacon usa Content-Type "text/plain" ou Blob — passar como Blob
      // pra forçar JSON parsing no servidor (text() depois JSON.parse)
      const blob = new Blob([body], { type: "application/json" })
      navigator.sendBeacon(TRACK_ENDPOINT, blob)
    } catch (err) {
      console.warn("[telemetry] beacon failed", err)
    }
  }

  // ─── Internos ────────────────────────────────────────────────────────────

  private getOrCreateSessionId(): string {
    if (typeof sessionStorage === "undefined") {
      return crypto.randomUUID()
    }
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem(SESSION_STORAGE_KEY, id)
    }
    return id
  }

  private async getJwt(): Promise<string | null> {
    const now = Date.now()
    if (this.cachedJwt && now < this.jwtExpiresAt) {
      return this.cachedJwt
    }
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token ?? null
    if (token) {
      this.cachedJwt = token
      // Cacha por 50 min (token Supabase dura 1h)
      this.jwtExpiresAt = now + 50 * 60 * 1000
    }
    return token
  }

  private startFlushTimer(): void {
    if (typeof window === "undefined") return
    this.flushTimer = window.setInterval(() => {
      void this.flush()
    }, FLUSH_INTERVAL_MS)
  }

  private stopFlushTimer(): void {
    if (this.flushTimer !== null) {
      window.clearInterval(this.flushTimer)
      this.flushTimer = null
    }
  }
}

export const telemetry = new TelemetryClient()
